/**
 * Microsoft Clarity session stitching utility
 * Uses Clarity's Identify API to tie sessions together across devices and browsers
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api#customize-your-identifiers
 */

// Store pending user to identify once Clarity loads
let pendingUser = null;
let clarityReady = false;

/**
 * Check if Clarity is loaded and ready
 */
function isClarityReady() {
  return typeof window !== 'undefined' && typeof window.clarity === 'function';
}

/**
 * Execute the actual Clarity identify call
 */
function executeIdentify(user) {
  if (!isClarityReady() || !user || !user.uid) {
    return false;
  }

  try {
    const customId = user.uid;
    const friendlyName = user.displayName || user.email || 'User';

    // Call Clarity's identify API
    window.clarity('identify', String(customId), undefined, undefined, String(friendlyName));

    // Also set user email as a custom tag for easier filtering in dashboard
    if (user.email) {
      window.clarity('set', 'user_email', user.email);
      window.clarity('set', 'user_id', customId);
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Initialize Clarity ready listener
 * This handles the race condition where auth loads before Clarity
 */
function initClarityReadyListener() {
  if (typeof window === 'undefined' || clarityReady) {
    return;
  }

  // If Clarity is already loaded, mark as ready
  if (isClarityReady()) {
    clarityReady = true;
    // Process any pending user
    if (pendingUser) {
      executeIdentify(pendingUser);
      pendingUser = null;
    }
    return;
  }

  // Poll for Clarity to be ready (it loads async)
  const checkInterval = setInterval(() => {
    if (isClarityReady()) {
      clarityReady = true;
      clearInterval(checkInterval);

      // Process any pending user
      if (pendingUser) {
        executeIdentify(pendingUser);
        pendingUser = null;
      }
    }
  }, 100);

  // Stop polling after 10 seconds to avoid infinite loop
  setTimeout(() => {
    clearInterval(checkInterval);
  }, 10000);
}

// Initialize the listener on module load
if (typeof window !== 'undefined') {
  initClarityReadyListener();
}

/**
 * Identify user with Clarity to enable session stitching
 * This ties all sessions together for the same user across devices/browsers
 * @param {Object|null} user - User object with uid, email, and displayName
 */
export function identifyClarityUser(user) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!user || !user.uid) {
    pendingUser = null;
    return;
  }

  if (isClarityReady()) {
    const success = executeIdentify(user);
    // If first attempt fails, retry after a short delay
    if (!success) {
      setTimeout(() => executeIdentify(user), 1000);
    }
  } else {
    // Store user to identify once Clarity loads
    pendingUser = user;
    initClarityReadyListener();
  }
}

/**
 * Set custom tags in Clarity for better filtering/segmentation
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export function setClarityTag(key, value) {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  try {
    window.clarity('set', key, value);
  } catch (error) {
    console.warn('Error setting Clarity tag:', error);
  }
}

/**
 * Upgrade the session priority in Clarity
 * Use this for important user actions you want to prioritize in recordings
 * @param {string} reason - Reason for upgrading (shown in dashboard)
 */
export function upgradeClaritySession(reason) {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  try {
    window.clarity('upgrade', reason);
  } catch (error) {
    console.warn('Error upgrading Clarity session:', error);
  }
}
